import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button, TextField, Box, Grid, Card, CardContent, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

import NavBar from '../../structure/NavBar';
import { NavBarTop } from '../../structure/NavBarTop';
import {
  updateTopHeader,
  updateLoading,
} from '../../../redux/features/common/commonSlice';
import urls from '../../constants/apiurl';
import ApiCaller from '../../constants/ApiCaller';
import { showDangerToast, showToast } from '../../constants/toaster';
import InsufficientPermission from '../../custom/InSufficientPermission';

export const AddEditDiscountVoucher = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Get ID from URL for edit mode
  const location = useLocation(); // Get navigation state
  const dispatch = useDispatch();
  const userData = useSelector((state) => state?.userData);
  const userPermissions = useSelector((state) => state?.permission);

  // Form state
  const [formData, setFormData] = useState({
    id: '',
    discountPercentage: '',
    price: '',
    maxDiscountAmount: '',
    minOrderAmount: '',
    startDate: null,
    endDate: null,
  });

  const [errors, setErrors] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);

  // Permission checks
  let currentModulePermission;
  let addPermission = true;
  let editPermission = true;

  if (userPermissions?.length > 0) {
    currentModulePermission = userPermissions?.find(
      (permission) => permission?.name === 'DISCOUNT_VOUCHERS'
    );
    if (currentModulePermission) {
      addPermission = currentModulePermission?.add;
      editPermission = currentModulePermission?.edit;
    }
  }

  useEffect(() => {
    if (id) {
      setIsEditMode(true);
      dispatch(updateTopHeader('Edit Discount Voucher'));
      
      // Get voucher data from navigation state
      const voucherData = location.state?.voucherData;
      if (voucherData) {
        setFormData({
          id: voucherData.id || '',
          discountPercentage: voucherData.discountPercentage || '',
          price: voucherData.price || '',
          maxDiscountAmount: voucherData.maxDiscountAmount || '',
          minOrderAmount: voucherData.minOrderAmount || '',
          startDate: voucherData.startDate ? dayjs(voucherData.startDate) : null,
          endDate: voucherData.endDate ? dayjs(voucherData.endDate) : null,
        });
      } else {
        // If no data in state, redirect back to list
        showDangerToast('Voucher data not found. Please try again.');
        navigate('/discount-vouchers-manager');
      }
    } else {
      dispatch(updateTopHeader('Add Discount Voucher'));
    }
  }, [id, location.state, dispatch, navigate]);

  // Validation function
  const validateForm = () => {
    const newErrors = {};

    if (!formData.discountPercentage || formData.discountPercentage <= 0) {
      newErrors.discountPercentage = 'Discount percentage is required and must be greater than 0';
    } else if (formData.discountPercentage > 100) {
      newErrors.discountPercentage = 'Discount percentage cannot exceed 100%';
    }

    if (!formData.price || formData.price <= 0) {
      newErrors.price = 'Price is required and must be greater than 0';
    }

    if (!formData.maxDiscountAmount || formData.maxDiscountAmount <= 0) {
      newErrors.maxDiscountAmount = 'Max discount amount is required and must be greater than 0';
    }

    if (!formData.minOrderAmount || formData.minOrderAmount <= 0) {
      newErrors.minOrderAmount = 'Min order amount is required and must be greater than 0';
    }

    // Validate that max discount is reasonable compared to price
    if (formData.maxDiscountAmount && formData.price && 
        parseFloat(formData.maxDiscountAmount) > parseFloat(formData.price)) {
      newErrors.maxDiscountAmount = 'Max discount amount cannot be greater than voucher price';
    }

    // Validate dates if provided
    if (formData.startDate && formData.endDate) {
      if (dayjs(formData.endDate).isBefore(dayjs(formData.startDate))) {
        newErrors.endDate = 'End date must be after start date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  // Handle date changes
  const handleDateChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showDangerToast('Please fix the validation errors');
      return;
    }

    try {
      dispatch(updateLoading(true));

      // Prepare data for API
      const apiData = {
        discountPercentage: parseFloat(formData.discountPercentage),
        price: parseFloat(formData.price),
        maxDiscountAmount: parseFloat(formData.maxDiscountAmount),
        minOrderAmount: parseFloat(formData.minOrderAmount),
        startDate: formData.startDate ? dayjs(formData.startDate).format('YYYY-MM-DD') + ' 00:00:00' : '',
        endDate: formData.endDate ? dayjs(formData.endDate).format('YYYY-MM-DD') + ' 23:59:59' : '',
      };

      // Add id for update operations
      if (isEditMode && formData.id) {
        apiData.id = formData.id;
      }

      let path = urls.addEditDiscountVoucher;
      let res = await ApiCaller.post(apiData, path, userData?.access_token);
      const response = await res.response.json();

      if (response.status === 200 || response.status === '200') {
        dispatch(updateLoading(false));
        showToast(
          response?.message || 
          `Discount voucher ${isEditMode ? 'updated' : 'created'} successfully`
        );
        navigate('/discount-vouchers-manager');
      } else {
        dispatch(updateLoading(false));
        showDangerToast(
          response?.message || 
          `Failed to ${isEditMode ? 'update' : 'create'} discount voucher`
        );
      }
    } catch (error) {
      dispatch(updateLoading(false));
      console.error('Error saving discount voucher:', error);
      showDangerToast('An unexpected error occurred while saving');
    }
  };

  // Check permissions
  if (isEditMode && !editPermission) {
    return <InsufficientPermission />;
  }
  if (!isEditMode && !addPermission) {
    return <InsufficientPermission />;
  }

  return (
    <>
      <NavBar />
      <div className="main-content" id="panel">
        <NavBarTop />
        <div className="header bg-primary pb-6">
          <div className="container-fluid">
            <div className="header-body">
              <div className="row align-items-center py-4">
                <div className="col-lg-6 col-7">
                  <h6 className="h2 text-white d-inline-block mb-0">
                    {isEditMode ? 'Edit' : 'Add'} Discount Voucher
                  </h6>
                </div>
                <div className="col-lg-6 col-5 text-right">
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => navigate('/discount-vouchers-manager')}
                  >
                    Back to List
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="container-fluid mt--6">
          <div className="row justify-content-center">
            <div className="col-xl-8 col-lg-10">
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Typography variant="h6" component="h2">
                      {isEditMode ? 'Edit Voucher Details' : 'Create New Voucher'}
                    </Typography>
                    {isEditMode && (
                      <Box sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        px: 2, 
                        py: 0.5, 
                        borderRadius: 1,
                        fontSize: '0.875rem'
                      }}>
                        Edit Mode
                      </Box>
                    )}
                  </Box>
                  
                  <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                    <Grid container spacing={3}>
                      {/* Discount Percentage */}
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Discount Percentage (%)"
                          type="number"
                          value={formData.discountPercentage}
                          onChange={(e) => handleInputChange('discountPercentage', e.target.value)}
                          error={!!errors.discountPercentage}
                          helperText={errors.discountPercentage}
                          inputProps={{ min: 0, max: 100, step: 0.01 }}
                          required
                        />
                      </Grid>

                      {/* Price */}
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Voucher Price ($)"
                          type="number"
                          value={formData.price}
                          onChange={(e) => handleInputChange('price', e.target.value)}
                          error={!!errors.price}
                          helperText={errors.price}
                          inputProps={{ min: 0, step: 0.01 }}
                          required
                        />
                      </Grid>

                      {/* Max Discount Amount */}
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Max Discount Amount ($)"
                          type="number"
                          value={formData.maxDiscountAmount}
                          onChange={(e) => handleInputChange('maxDiscountAmount', e.target.value)}
                          error={!!errors.maxDiscountAmount}
                          helperText={errors.maxDiscountAmount}
                          inputProps={{ min: 0, step: 0.01 }}
                          required
                        />
                      </Grid>

                      {/* Min Order Amount */}
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Min Order Amount ($)"
                          type="number"
                          value={formData.minOrderAmount}
                          onChange={(e) => handleInputChange('minOrderAmount', e.target.value)}
                          error={!!errors.minOrderAmount}
                          helperText={errors.minOrderAmount}
                          inputProps={{ min: 0, step: 0.01 }}
                          required
                        />
                      </Grid>

                      {/* Start Date */}
                      <Grid item xs={12} md={6}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            label="Start Date (Optional)"
                            value={formData.startDate}
                            onChange={(newValue) => handleDateChange('startDate', newValue)}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                fullWidth
                                error={!!errors.startDate}
                                helperText={errors.startDate}
                              />
                            )}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                error: !!errors.startDate,
                                helperText: errors.startDate,
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </Grid>

                      {/* End Date */}
                      <Grid item xs={12} md={6}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            label="End Date (Optional)"
                            value={formData.endDate}
                            onChange={(newValue) => handleDateChange('endDate', newValue)}
                            minDate={formData.startDate || undefined}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                fullWidth
                                error={!!errors.endDate}
                                helperText={errors.endDate}
                              />
                            )}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                error: !!errors.endDate,
                                helperText: errors.endDate,
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </Grid>

                      {/* Action Buttons */}
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                          <Button
                            variant="outlined"
                            color="secondary"
                            onClick={() => navigate('/discount-vouchers-manager')}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                          >
                            {isEditMode ? 'Update' : 'Create'} Voucher
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddEditDiscountVoucher;